import React, { Fragment, useEffect, useState } from "react";
import { Col, Modal, Row } from "react-bootstrap";
import { AgGridReact } from "ag-grid-react";
import Select from "react-select";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { voucherDelete, voucherList, voucherView } from "../../../services/accounts";

const VOUCHER_TYPES = [
  { value: "",        label: "All Types" },
  { value: "Receipt", label: "Receipt"   },
  { value: "Payment", label: "Payment"   },
  { value: "Journal", label: "Journal"   },
  { value: "Contra",  label: "Contra"    },
];

const TYPE_BADGE = {
  Receipt: "badge bg-success",
  Payment: "badge bg-danger",
  Journal: "badge bg-warning text-dark",
  Contra:  "badge bg-info",
};

export default function VoucherList() {
  const navigate = useNavigate();
  const [list, setList]             = useState([]);
  const [filterType, setFilterType] = useState(VOUCHER_TYPES[0]);
  const [fromDate, setFromDate]     = useState("");
  const [toDate, setToDate]         = useState("");
  const [detailShow, setDetailShow] = useState(false);
  const [detail, setDetail]         = useState(null);

  useEffect(() => { loadList(); }, []);

  function loadList() {
    const temp = new FormData();
    if (filterType?.value) temp.append("voucher_type", filterType.value);
    if (fromDate) temp.append("from_date", fromDate);
    if (toDate)   temp.append("to_date", toDate);
    if (localStorage.getItem("Role") === "1") {
      temp.append("academy_id", localStorage.getItem("AAcademy") || "");
    }
    voucherList(temp).then((res) => {
      if (res?.status) setList(res.data);
      else toast.error(res?.message || "Failed to load vouchers");
    });
  }

  function handleView(id) {
    voucherView(id).then((res) => {
      if (res?.status) { setDetail(res.data); setDetailShow(true); }
    });
  }

  function handleDelete(id) {
    Swal.fire({
      text: "Delete this voucher?", icon: "warning", showCancelButton: true,
      confirmButtonColor: "#ac0404", cancelButtonColor: "#6259ca",
      confirmButtonText: "Delete", allowOutsideClick: false,
    }).then((result) => {
      if (result.isConfirmed) {
        voucherDelete(id).then((res) => {
          if (res?.status) { toast.success(res.message); loadList(); }
          else toast.error(res?.message || "Cannot delete auto-generated vouchers");
        });
      }
    });
  }

  const columnDefs = [
    { headerName: "Date",       field: "voucher_date", sortable: true, filter: true, floatingFilter: true, resizable: true, width: 120 },
    { headerName: "Voucher No", field: "voucher_no",   sortable: true, filter: true, floatingFilter: true, resizable: true },
    {
      headerName: "Type", field: "voucher_type", width: 120, resizable: true,
      cellRenderer: (params) => <span className={TYPE_BADGE[params.value] || "badge bg-secondary"}>{params.value}</span>,
    },
    { headerName: "Narration", field: "narration", resizable: true, cellStyle: { textAlign: "left" } },
    {
      headerName: "Source", field: "source_type", width: 110, resizable: true,
      cellRenderer: (params) => <span className="badge bg-light text-dark text-capitalize">{params.value}</span>,
    },
    {
      headerName: "Actions", field: "id", width: 120, filter: false, resizable: true,
      cellRenderer: (params) => (
        <div className="text-center">
          <a onClick={() => handleView(params.value)} className="btn-sm btn-info me-2"><i className="fa fa-eye" /></a>
          <a onClick={() => handleDelete(params.value)} className="btn-sm btn-danger"><i className="fa fa-trash" /></a>
        </div>
      ),
    },
  ];

  return (
    <Fragment>
      <div className="page-header reported-header">
        <div>
          <h2 className="main-content-title tx-24 mg-b-5">Voucher List</h2>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a>Accounts</a></li>
            <li className="breadcrumb-item active" aria-current="page">Voucher List</li>
          </ol>
        </div>
      </div>

      <div className="content-body">
        <div className="container-fluid">
          <div className="card custom-card mg-b-20">
            <div className="card-body">
              <Row className="row-sm mx-0">
                <Col xxl={4} lg={4} md={4}>
                  <div className="mb-3">
                    <label className="form-label">From Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                  </div>
                </Col>
                <Col xxl={4} lg={4} md={4}>
                  <div className="mb-3">
                    <label className="form-label">To Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={toDate}
                      min={fromDate || undefined}
                      onChange={(e) => setToDate(e.target.value)}
                    />
                  </div>
                </Col>
                <Col xxl={4} lg={4} md={4}>
                  <div className="mb-3">
                    <label className="form-label">Voucher Type</label>
                    <Select
                      className="default-select"
                      options={VOUCHER_TYPES}
                      value={filterType}
                      onChange={(e) => setFilterType(e)}
                    />
                  </div>
                </Col>
                <Col xxl={12} lg={12} md={12}>
                  <div className="mb-3">
                    <a onClick={loadList} className="btn btn-success me-2">Search</a>
                    <a onClick={() => navigate("/accounts/receipt-voucher")} className="btn btn-success">
                      <i className="fa fa-plus me-1" /> New Voucher
                    </a>
                  </div>
                </Col>
              </Row>
            </div>
          </div>

          <div className="card custom-card mg-b-20">
            <div className="card-body">
              <div className="ag-theme-alpine ag-style">
                <AgGridReact
                  columnDefs={columnDefs}
                  rowData={list}
                  pagination
                  paginationPageSize={15}
                  suppressDragLeaveHidesColumns
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Voucher Detail Modal */}
      <Modal show={detailShow} onHide={() => setDetailShow(false)} size="lg">
        <Modal.Header>
          <h4>{detail?.voucher_type} Voucher — {detail?.voucher_no}</h4>
        </Modal.Header>
        <Modal.Body>
          {detail && (
            <>
              <Row className="mb-3">
                <Col lg={4}><strong>Date:</strong> {detail.voucher_date}</Col>
                <Col lg={4}><strong>Type:</strong> <span className={TYPE_BADGE[detail.voucher_type]}>{detail.voucher_type}</span></Col>
                <Col lg={4}><strong>Source:</strong> <span className="text-capitalize">{detail.source_type}</span></Col>
              </Row>
              {detail.narration && <p className="mb-3"><strong>Narration:</strong> {detail.narration}</p>}
              <table className="table table-bordered table-sm">
                <thead className="thead-light">
                  <tr>
                    <th>Ledger</th>
                    <th>Group</th>
                    <th className="text-end">Debit</th>
                    <th className="text-end">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.items?.map((item, i) => (
                    <tr key={i}>
                      <td>{item.ledger?.ledger_name}</td>
                      <td>{item.ledger?.group?.group_name}</td>
                      <td className="text-end">{parseFloat(item.debit_amount) > 0 ? parseFloat(item.debit_amount).toFixed(2) : "-"}</td>
                      <td className="text-end">{parseFloat(item.credit_amount) > 0 ? parseFloat(item.credit_amount).toFixed(2) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="fw-bold">
                    <td colSpan={2}>Total</td>
                    <td className="text-end">{detail.items?.reduce((s, r) => s + parseFloat(r.debit_amount || 0), 0).toFixed(2)}</td>
                    <td className="text-end">{detail.items?.reduce((s, r) => s + parseFloat(r.credit_amount || 0), 0).toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <a onClick={() => setDetailShow(false)} className="btn btn-secondary">Close</a>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
}
